import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sparkles,
  Users,
  MessageCircle,
  Heart,
  Loader2,
  Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Early Bird page – linked from the follow-up e-mail sent after the seminar.
 * URL: /early-bird
 * Contains: offer info + interest form.
 * On submit: navigates to /early-bird/tack.
 */
export default function EarlyBird() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Early Bird · Aminah Ådin";
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Fyll i namn och e-post.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/early-bird`, form);
      navigate("/early-bird/tack");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Något gick fel. Försök igen om en stund.";
      toast.error(typeof msg === "string" ? msg : "Något gick fel. Försök igen.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#2C2825]" data-testid="earlybird-page">
      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#F3EFEA] border border-[#DCD5CE] px-4 py-2 mb-8">
            <Sparkles className="h-4 w-4 text-[#A98C78]" />
            <span className="text-xs tracking-[0.25em] uppercase text-[#8C7362]">
              Early Bird-erbjudande
            </span>
          </div>
          <h1
            data-testid="earlybird-headline"
            className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-[#2C2825] leading-[1.05]"
          >
            Fortsätt resan – till ett{" "}
            <em className="not-italic text-[#8C7362]">mjukare</em> pris.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-[#5C5651] leading-relaxed max-w-2xl mx-auto">
            Tack för att du var med under seminariet. För dig som vill gå
            djupare öppnar jag nu upp ett fåtal platser i mitt coachingprogram
            – till ett Early Bird-pris som gäller under en begränsad tid.
          </p>
        </div>
      </section>

      {/* Two program options */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-6 md:gap-7">
          <OfferCard
            icon={Users}
            tag="Grupprogram"
            title="Tillsammans i en trygg liten cirkel"
            points={[
              "Digitalt program med ca 15 deltagare",
              "Möts varje vecka med varma samtal & övningar",
              "Verktyg för att möta sorg, skam och nedstämdhet",
              "Läk i ett medmänskligt sammanhang",
            ]}
          />
          <OfferCard
            icon={MessageCircle}
            tag="Enskilda samtal"
            title="En egen plats – bara för dig"
            points={[
              "Individuella samtal anpassade efter dina behov",
              "Lyssnande, närvaro och konkreta verktyg",
              "I din takt, på dina villkor",
              "För dig som vill arbeta djupare och privat",
            ]}
          />
        </div>
        <p className="mt-10 text-center text-sm text-[#8C7362] max-w-xl mx-auto px-6">
          Ingen betalning sker här – du fyller bara i en intresseanmälan så
          hör Aminah av sig personligen via mejl med detaljer och faktura.
        </p>
      </section>

      {/* Interest form */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-[#F3EFEA] border border-[#E6DFD7] rounded-3xl p-8 md:p-12">
            <div className="text-center mb-8">
              <p className="text-xs tracking-[0.3em] uppercase text-[#8C7362] mb-4">
                Intresseanmälan
              </p>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#2C2825] leading-tight">
                Anmäl ditt intresse
              </h2>
              <p className="mt-4 text-sm text-[#5C5651]">
                Så återkommer jag med detaljer inom kort.
              </p>
            </div>

            <form
              onSubmit={onSubmit}
              data-testid="earlybird-form"
              className="flex flex-col gap-4"
            >
              <Field label="Namn *">
                <Input
                  data-testid="earlybird-name"
                  type="text"
                  placeholder="För- och efternamn"
                  value={form.name}
                  onChange={set("name")}
                  required
                  className="h-12 rounded-full bg-white border-[#DCD5CE] px-5 focus-visible:ring-[#A98C78] focus-visible:ring-offset-0"
                  autoComplete="name"
                />
              </Field>

              <Field label="E-post *">
                <Input
                  data-testid="earlybird-email"
                  type="email"
                  placeholder="Din e-postadress"
                  value={form.email}
                  onChange={set("email")}
                  required
                  className="h-12 rounded-full bg-white border-[#DCD5CE] px-5 focus-visible:ring-[#A98C78] focus-visible:ring-offset-0"
                  autoComplete="email"
                />
              </Field>

              <Field label="Telefon (valfritt)">
                <Input
                  data-testid="earlybird-phone"
                  type="tel"
                  placeholder="Ditt telefonnummer"
                  value={form.phone}
                  onChange={set("phone")}
                  className="h-12 rounded-full bg-white border-[#DCD5CE] px-5 focus-visible:ring-[#A98C78] focus-visible:ring-offset-0"
                  autoComplete="tel"
                />
              </Field>

              <Field label="Vad är du intresserad av?">
                <Select
                  value={form.interest}
                  onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}
                >
                  <SelectTrigger
                    data-testid="earlybird-interest"
                    className="h-12 rounded-full bg-white border-[#DCD5CE] px-5 focus:ring-[#A98C78] focus:ring-offset-0"
                  >
                    <SelectValue placeholder="Välj alternativ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grupp">Grupprogrammet</SelectItem>
                    <SelectItem value="enskilt">Enskilda samtal</SelectItem>
                    <SelectItem value="vet_ej">Vet inte ännu – vill veta mer</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Meddelande (valfritt)">
                <Textarea
                  data-testid="earlybird-message"
                  placeholder="Berätta gärna kort vad du hoppas få ut av programmet."
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  className="rounded-2xl bg-white border-[#DCD5CE] px-5 py-4 focus-visible:ring-[#A98C78] focus-visible:ring-offset-0 resize-none"
                />
              </Field>

              <Button
                type="submit"
                disabled={loading}
                data-testid="earlybird-submit"
                className="mt-2 h-14 rounded-full bg-[#A98C78] hover:bg-[#8C7362] text-white tracking-[0.18em] uppercase text-xs font-semibold px-8 transition-all disabled:opacity-70"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Skickar...
                  </span>
                ) : (
                  "Skicka intresseanmälan"
                )}
              </Button>
              <p className="text-[11px] text-[#8C7362] text-center mt-2 leading-relaxed">
                Ingen betalning sker här – Aminah hör av sig via mejl efter att du skickat.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-[#E6DFD7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-serif-display text-xl text-[#2C2825]">Aminah Ådin</p>
          <p className="text-xs text-[#8C7362]">© {new Date().getFullYear()} Aminah Ådin</p>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs tracking-[0.2em] uppercase text-[#8C7362]">
        {label}
      </span>
      {children}
    </label>
  );
}

function OfferCard({ icon: Icon, tag, title, points }) {
  return (
    <div className="bg-white border border-[#E6DFD7] rounded-3xl p-8 md:p-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-full bg-[#F3EFEA] border border-[#DCD5CE] flex items-center justify-center">
          <Icon className="h-5 w-5 text-[#A98C78]" />
        </div>
        <span className="text-xs tracking-[0.25em] uppercase text-[#8C7362]">
          {tag}
        </span>
      </div>
      <h3 className="font-serif-display text-2xl md:text-3xl text-[#2C2825] leading-snug">
        {title}
      </h3>
      <ul className="mt-5 space-y-3">
        {points.map((p, i) => (
          <li key={i} className="flex items-start gap-3 text-[#5C5651]">
            <Check className="h-4 w-4 text-[#A98C78] flex-shrink-0 mt-1" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
